import React, { Component } from "react";
import { Icon } from "@material-ui/core";
import classNames from "classnames";
import moment from "moment";

import { DialogTitle, MenuItem, Select } from "@material-ui/core";

import db from "js/db";
import { ds } from "js/core/models/dataService";
import { app } from "js/namespaces";
import { isPPTAddin } from "js/config";
import Api from "js/core/api";
import { trackActivity } from "js/core/utilities/utilities";
import { CustomerType } from "common/constants";
import BillingDataService from "js/react/views/UserOptions/dataservices/billingDataService";
import Adapter from "js/core/storage/adapter";
import FirebaseAdapter from "js/core/storage/firebaseAdapter";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { User } from "js/core/models/user";
import sanitizeRelativeUrl from "common/utils/sanitizeRelativeUrl";

import { Gap10, Gap20, Gap5 } from "js/react/components/Gap";
import { BeautifulDialog, DialogContent, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import Spinner from "js/react/components/Spinner";

import SuccessfulPurchase from "./SuccessfulPurchase";
import CheckoutFormInner from "./CheckoutFormInner";

import "css/billing.scss";
import * as analytics from "js/analytics";

Adapter.setDefaultClass(FirebaseAdapter, { readonly: true, autoSync: false });

class CheckoutDialog extends Component {
    constructor(props) {
        super();
        this.state = {
            billingInterval: props.defaultBillingInterval ?? "year",
            successfulPurchase: false,
            isSubmitting: false,
            availableReferralCredit: 0,
            productDisplayName: "",
            alreadySubscribed: false
        };
    }

    async componentDidMount() {
        let trialDetailPromise = Api.trialDetail.get({
            t: new Date().getTime()
        });

        let subscriptionPromise = Api.subscriptions.get({
            customer_type: CustomerType.INDIVIDUAL
        });

        const [trialDetailResponse, subscriptionResponse] = await Promise.all([trialDetailPromise, subscriptionPromise]);
        let { currentPlan, hasTakenTrial, analyticsCurrentPlan } = trialDetailResponse;

        //If user has an individual subscription and their plan is not cancelled show dialog to send them back to library.
        if (subscriptionResponse.length && subscriptionResponse[0].id && !subscriptionResponse[0].cancel_at_period_end) {
            this.setState({ alreadySubscribed: true });
            ShowMessageDialog({
                title: "Are you sure about this?",
                message: "You are already paying for a Pro plan. Let’s take you to your presentation library.",
                onClick: () => {
                    window.location = "/";
                },
                onClose: () => {
                    window.location = "/";
                }
            });
        }

        this.setState({ currentPlan, hasTakenTrial });

        // sign into all Firebase app instances
        await db.updateCurrentUser(this.props.firebaseUser);

        // load the User model
        app.user = new User({ id: this.props.firebaseUser.uid }, { autoLoad: false });
        ds.checkoutSetup();
        await app.user.load();
        await analytics.identify(app.user.getAuthUser());

        app.user.analyticsPersonalPlan = analyticsCurrentPlan;

        const data = await Api.referrals.get();
        this.setState({ availableReferralCredit: data.totalAvailable });

        // fetch pricing from Stripe
        let prices;
        if (this.props.priceId && this.props.priceId.includes(" ")) {
            const priceIds = this.props.priceId.split(" ");

            const priceData = [
                await Api.prices.get({ id: priceIds[0] }),
                await Api.prices.get({ id: priceIds[1] })
            ];

            const month = priceData.find(p => p.recurring.interval === "month");
            const year = priceData.find(p => p.recurring.interval === "year");

            if (!month || !year) {
                throw new Error("expected monthly and yearly prices'");
            }

            prices = {
                month,
                year,
            };
        } else if (this.props.priceId) {
            prices = {
                year: await Api.prices.get({ id: this.props.priceId })
            };
        } else {
            const priceIds = BillingDataService.getPrices("pro");
            prices = {
                month: await Api.prices.get({ id: priceIds.month }),
                year: await Api.prices.get({ id: priceIds.year })
            };
        }

        this.setState({
            prices,
            productDisplayName: this.props.productDisplayName ??
                prices.year.nickname.substring(0, prices.year.nickname.indexOf("-"))
        });

        const params = new URLSearchParams(window.location.search);
        const testId = params.get("ex");

        const props = {
            "current_status": currentPlan,
            "workspace_id": "personal",
            price_id: this.props.priceId,
            plan_display_name: this.state.productDisplayName,
            test_id: testId,
        };
        trackActivity("Upgrade", "BillingIntentStart", testId, null, props, { audit: true });
    }

    get amount() {
        const { billingInterval, promo, prices } = this.state;
        const dollars = prices[billingInterval].unit_amount / 100;
        return BillingDataService.applyCoupon(dollars, promo);
    }

    refreshApp = () => {
        setTimeout(() => {
            const continueUrl = sanitizeRelativeUrl(new URLSearchParams(window.location.search).get("continue") ?? "/");
            window.location.href = continueUrl;
        }, 50);
    };

    handleOnSuccess = async () => {
        const { billingInterval, currentPlan } = this.state;

        let hasPresentations = ds && ds.presentations;
        if (trackActivity) {
            const subscriptions = await Api.subscriptions.get({
                customer_type: CustomerType.INDIVIDUAL
            });
            let slideCount = hasPresentations ? ds.presentations.getWorkspaceSlideCount("personal") : "N/A";
            const params = new URLSearchParams(window.location.search);
            const testId = params.get("ex");
            const eventProps = {
                billing_term: billingInterval,
                current_status: currentPlan,
                amount: this.amount,
                plan: "pro",
                current_slide_count: slideCount,
                subscription_id: subscriptions[0] ? subscriptions[0].id : null,
                workspace_id: "personal",
                promo_code: this.state.promo?.code,
                price_id: this.props.priceId,
                plan_display_name: this.state.productDisplayName,
                test_id: testId,
            };
            trackActivity("Upgrade", "BillingIntentComplete", testId, null, eventProps, { audit: true });
            await Api.klaviyoTrack.post({
                eventName: "Upgrade:BillingIntentComplete",
                eventProps,
            });
        }

        this.setState({ successfulPurchase: true, isSubmitting: false });
    };

    handleOnBeforeCharge = () => {
        this.setState({ isSubmitting: true });
    };

    handleOnFailure = () => {
        this.setState({ isSubmitting: false });
    };

    handlePromoChanged = promo => {
        this.setState({ promo });
    };

    render() {
        if (!this.state.prices || this.state.alreadySubscribed) {
            return null;
        }

        const { successfulPurchase, billingInterval, isSubmitting, prices, productDisplayName, hasTakenTrial } = this.state;
        const showTrial = !hasTakenTrial;

        const fullPrice = this.amount;
        const price = Math.max(fullPrice - (this.state.availableReferralCredit / 100), 0);

        let trialExpirationDate = moment()
            .add(14, "days")
            .format("MMMM Do YYYY");

        let nextBillingDate;
        if (billingInterval == "year") {
            nextBillingDate = moment()
                .add(1, "year")
                .format("MMMM Do YYYY");
        } else {
            nextBillingDate = moment()
                .add(1, "month")
                .format("MMMM Do YYYY");
        }

        if (successfulPurchase) {
            return (
                <SuccessfulPurchase
                    productDisplayName={productDisplayName ?? "Pro"}
                    showTrial={showTrial}
                    trialExpirationDate={trialExpirationDate}
                    additionalMessage={isPPTAddin ? "The changes will be applied next time you create or edit a slide." : null}
                    onCallToActionClick={() => {
                        if (isPPTAddin) {
                            this.props.closeDialog();
                        } else {
                            this.refreshApp();
                        }
                    }}
                    callToActionText={isPPTAddin ? "Done" : "Refresh"}
                />
            );
        } else {
            return (
                <BeautifulDialog
                    preventClose={isSubmitting}
                    closeDialog={this.props.closeDialog}
                    hideBackdrop={this.props.hideBackdrop}
                    style={this.props.style}
                >
                    <DialogTitle>
                        Upgrade to Beautiful.ai <span color="#11a9e2">{productDisplayName}</span>
                    </DialogTitle>
                    <DialogContent>
                        <div
                            id="checkout_form_container"
                            className={classNames({
                                disabled: isSubmitting
                            })}
                        >
                            {isSubmitting && <Spinner />}
                            {showTrial ? (
                                <h3>
                                    Start your 14-day free trial. You can cancel anytime during your trial and you won't
                                    be charged anything.
                                </h3>
                            ) : (
                                <h3>
                                    We're are sorry but you are not eligible for a 14-day free trial because you have
                                    already had a trial in the past.
                                </h3>
                            )}

                            <Gap10 />
                            {prices.month && (
                                <>
                                    <Gap5 />
                                    <div className="label">Choose Plan</div>

                                    <Select
                                        SelectDisplayProps={{ style: { textTransform: "capitalize" } }}
                                        style={{ width: "100%" }}
                                        variant="outlined"
                                        value={billingInterval}
                                        inputProps={{ className: "select-input" }}
                                        onChange={event => {
                                            this.setState({
                                                billingInterval: event.target.value
                                            });
                                        }}
                                    >
                                        <MenuItem className="menu-item-with-badge" value="year">
                                            Annual Plan - ${prices.year.unit_amount / 12 / 100}/month
                                            <div className="menu-item-badge">Best Value</div>
                                        </MenuItem>
                                        <MenuItem value="month">Monthly Plan - ${prices.month.unit_amount / 100}/month</MenuItem>
                                    </Select>
                                </>
                            )}

                            {!prices.month && (
                                <b>Annual Plan - ${prices.year.unit_amount / 12 / 100}/month</b>
                            )}

                            {showTrial ? (
                                <h3 style={{ fontSize: 14 }}>
                                    You will not be charged today.
                                    Your first bill of <strong>${price.toFixed(2)}</strong> will be due on{" "}
                                    <strong>{trialExpirationDate}</strong>.
                                </h3>
                            ) : (
                                <h3 style={{ fontSize: 14 }}>
                                    You will be charged <strong>${price.toFixed(2)}</strong> today. Your next bill will be due on{" "}
                                    <strong>{nextBillingDate}</strong>.
                                </h3>
                            )}
                            {this.state.availableReferralCredit > 0 && (
                                <div style={{ paddingTop: 10, paddingBottom: 10 }}>
                                    <Icon style={{ color: "#fa0", fontSize: "1.25em", verticalAlign: "sub" }}>monetization_on</Icon>{" "}
                                    You saved <b>${Math.min(this.state.availableReferralCredit / 100, fullPrice)}</b> from referral credit.
                                </div>
                            )}
                            <Gap20 />

                            <CheckoutFormInner
                                productName="pro"
                                billingInterval={billingInterval}
                                productPrice={this.props.priceId && this.state.prices && this.state.prices[billingInterval].id}
                                amount={fullPrice}
                                customerType="individual"
                                onSuccess={this.handleOnSuccess}
                                onBeforeCharge={this.handleOnBeforeCharge}
                                onFailure={this.handleOnFailure}
                                onCancel={app.isConstrained && this.props.closeDialog}
                                submitLabel={showTrial ? "Start Free Trial" : "Upgrade Now"}
                                buttonStyle={{ marginTop: 30 }}
                                hasTakenTrial={!showTrial}
                                onPromoChanged={this.handlePromoChanged}
                            />
                        </div>
                    </DialogContent>
                </BeautifulDialog>
            );
        }
    }
}

export default withFirebaseUser(CheckoutDialog);
